import { useCallback, useEffect, useMemo, useState } from "react";
import {
  SchedulingDispatchPageService,
  type PageResponse,
  type SchedulingDispatchPageQuery,
  type SchedulingDispatchRow,
  type SchedulingDispatchSummary,
} from "@/services/scheduling-dispatch-page.service";

function emptyPage<T>(): PageResponse<T> {
  return { data: [], count: 0, error: null };
}

export function useSchedulingDispatchRowsPage(query: SchedulingDispatchPageQuery & { enabled?: boolean }) {
  const [state, setState] = useState<PageResponse<SchedulingDispatchRow>>(emptyPage());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setState(emptyPage());
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    void SchedulingDispatchPageService.getRowsPage(query).then((result) => {
      if (cancelled) return;
      setState(result);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [query.enabled, query.page, query.pageSize, query.search, query.filter, query.refreshToken]);

  return { ...state, loading };
}

export function useSchedulingDispatchSummary(query: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<SchedulingDispatchSummary | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    void SchedulingDispatchPageService.getSummary(query.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [query.enabled, query.refreshToken]);

  return { data, error, loading };
}

export function useSchedulingDispatchServerData(query: {
  enabled?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
  filter?: SchedulingDispatchPageQuery["filter"];
}) {
  const [refreshToken, setRefreshToken] = useState(0);
  const rowsPage = useSchedulingDispatchRowsPage({
    enabled: query.enabled,
    page: query.page ?? 0,
    pageSize: query.pageSize ?? 50,
    search: query.search,
    filter: query.filter,
    refreshToken,
  });
  const summary = useSchedulingDispatchSummary({ enabled: query.enabled, refreshToken });

  const rows = useMemo(() => rowsPage.data, [rowsPage.data]);
  const refresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  return {
    rows,
    count: rowsPage.count,
    error: rowsPage.error ?? summary.error,
    loading: rowsPage.loading || summary.loading,
    summary: summary.data,
    refresh,
  };
}
